// shared/src/components/GlobalNav.tsx

import React from 'react';
import { UserProfile } from '../types';
import { getAssetUrl } from '../utils/assets';

const APPS: AppMeta[] = [
    {
        id: 'attendance',
        name: 'Asistencia',
        path: '/',
        icon: getAssetUrl('/Logos/Logo CA-1.png'),
        devPort: 5182,
        prodUrl: '/control-asistencia/'
    },
    {
        id: 'inventory',
        name: 'Inventario',
        path: '/',
        icon: getAssetUrl('/Logos/Logo CI-1.png'),
        devPort: 3000,
        prodUrl: '/control-inventario/'
    },
    {
        id: 'expenses',
        name: 'Gastos de Obra',
        path: '/',
        icon: getAssetUrl('/Logos/Logo Gastos.png'),
        devPort: 3001,
        prodUrl: '/gastos/'
    },
    {
        id: 'presupuestos',
        name: 'Gestión de Presupuestos',
        path: '/',
        icon: getAssetUrl('/Logos/Logo Presupuestos.png'),
        devPort: 5176,
        prodUrl: '/presupuestos/'
    },
];

interface AppMeta {
    id: string;
    name: string;
    path: string;
    icon: string;
    devPort?: number;
    prodUrl?: string;
}

interface SwitcherProps {
    user: UserProfile | null;
    currentAppId: string;
    isCollapsed?: boolean;
}

export const GlobalNav: React.FC<SwitcherProps> = ({ user, currentAppId, isCollapsed = false }) => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    // Si no hay usuario, mostramos un estado básico de invitado
    const userRole = (user?.role || 'guest').toLowerCase();
    const userApps = user?.apps || [];

    // Admins y Owners ven todo. Invitados ven lo básico o la app actual.
    // Admins y Owners ven todo. Invitados ven lo básico o la app actual.
    // const isPowerUser = userRole === 'admin' || userRole === 'owner' || userRole === 'guest';

    // Logic update: Show ALL apps, but disable those without access.
    // We iterate over APPS directly.

    const getAppUrl = (app: AppMeta) => {
        // App "Presupuestos" is a prototype, always inactive for now unless specified
        // if (app.id === 'presupuestos') return undefined;

        if (isLocal && app.devPort) {
            // Fix: Apps now run on subpaths in dev too.
            // app.path is usually '/' for the dashboard.
            // We need to construct: http://localhost:5182/control-asistencia/
            // But app.prodUrl is /control-asistencia/.
            // So we can use prodUrl as the base?
            // Let's use specific logic or just rely on prodUrl being the path.

            // If app.prodUrl is defined (e.g. /control-asistencia/), we append it.
            // If app.path is '/', we might get double slash if we join carelessly.
            const subPath = app.prodUrl || '/';
            const cleanSubPath = subPath.endsWith('/') ? subPath.slice(0, -1) : subPath;
            return `http://localhost:${app.devPort}${cleanSubPath}${app.path}`;
        }
        // En producción usamos la URL absoluta si existe, o el path relativo como fallback
        return app.prodUrl || app.path;
    };

    return (
        <div className={`grid ${isCollapsed ? 'grid-cols-1 gap-3 py-3 px-1' : 'grid-cols-2 gap-4 py-4 px-4'} place-items-center w-full no-print border-t border-white/5 mt-auto select-none`}>
            {APPS.map(app => {
                const isActive = currentAppId === app.id;

                // Permission Check Logic (Based on Matrix v3)
                // Owner & Admin: All Access
                const isGodMode = userRole === 'owner' || userRole === 'admin';

                // Office: All Access EXCEPT specific restrictions handled inside apps. 
                // They can see all icons.
                const isOffice = userRole === 'office';

                // Resident: Asistencia & Inventario ONLY.
                const isResident = userRole === 'resident' || userRole === 'supervisor'; // explicit legacy support
                const residentAllowedNames = ['attendance', 'inventory'];

                // Auxiliary: Inventario ONLY.
                const isAuxiliary = userRole === 'auxiliary';
                const auxiliaryAllowedNames = ['inventory'];

                // Determine access
                let hasAccess = false;

                if (isGodMode || isOffice) {
                    hasAccess = true;
                } else if (isResident) {
                    hasAccess = residentAllowedNames.includes(app.id);
                } else if (isAuxiliary) {
                    hasAccess = auxiliaryAllowedNames.includes(app.id);
                } else {
                    // Fallback to strict explicit assignment from DB if role logic fails or is custom
                    hasAccess = userApps.includes(app.id);
                }

                // Presupuestos is always effectively disabled for navigation (no URL), 
                // but we might want to show it as "No access" or just "Coming Soon".
                const isAccessible = hasAccess;

                // Special Case: If it's the current app, users are already here, so it's accessible "visually" for the tab,
                // even if logic above says no (e.g. if they forced URL). 
                // But better to reflect true permission.
                // Keeping original logic: "OR app.id === currentAppId" to ensure the active tab is always 'lit'.
                const finalAccessible = isAccessible || app.id === currentAppId;

                return (
                    <a
                        key={app.id}
                        href={isAccessible ? getAppUrl(app) : undefined}
                        title={app.name}
                        onClick={(e) => {
                            if (!isAccessible || isActive) {
                                e.preventDefault();
                            }
                        }}
                        className={`relative group flex items-center justify-center p-1 transition-all duration-300 shrink-0 
                            ${isActive ? 'opacity-100 drop-shadow-md' : 'opacity-80 hover:opacity-100'} 
                            ${!finalAccessible ? 'cursor-not-allowed' : 'cursor-pointer'}
                        `}
                    >
                        <img
                            src={app.icon}
                            alt={app.name}
                            className={`object-contain shrink-0 transition-transform duration-300 
                                ${isCollapsed ? 'h-10 w-10' : 'h-12 w-12'}
                                ${isActive ? 'scale-110 brightness-110 drop-shadow-[0_0_5px_rgba(255,255,255,0.3)]' : 'grayscale-[20%] hover:scale-105 hover:grayscale-0'}
                            `}
                        />

                        {isActive && (
                            <div className="absolute -bottom-1 w-1.5 h-1.5 bg-cyan-400 rounded-full shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
                        )}
                    </a>
                );
            })}
        </div>
    );
};
